import { useEffect, useState, useCallback, useMemo } from 'react';
import { getPromoCouponCollection, postCouponClaim } from 'apis/promo';
import { ValidationError } from '@shared/services';
import { type CouponSheetModalPropTypes } from './CouponSheetModal';

interface UseCouponSheetModalPropTypes extends CouponSheetModalPropTypes {
    visible: boolean;
}

type CouponMap = Record<string, BuyerPromoCoupon>;

export function useCouponSheetModal(props: UseCouponSheetModalPropTypes) {
    const {
        visible,
        couponId: selectedCouponId,
        onChangeCouponId,
        shippingProfileId,
    } = props;

    const [isLoading, setLoading] = useState<boolean>(true);

    const [isFetching, setFetching] = useState<boolean>(true);

    const [couponMap, setCouponMap] = useState<CouponMap>({});

    const [error, setError] = useState<string>('');

    function getSelectedCouponCode() {
        return couponMap[selectedCouponId]?.code;
    }

    function handleSelectedCouponOnChange(couponId: string) {
        onChangeCouponId(couponId === selectedCouponId ? null : couponId);
    }

    const fetchCouponMap = useCallback(async () => {
        try {
            const resp = await getPromoCouponCollection({
                shipping_profile_id: shippingProfileId,
            });

            if (!resp) {
                setCouponMap({});

                return;
            }

            const formattedCouponMap = resp.data.reduce((acc, coupon) => {
                acc[coupon.id] = coupon;

                return acc;
            }, {});

            setCouponMap(formattedCouponMap);
        } catch (err) {
            console.error(err);
        }
    }, [shippingProfileId]);

    async function handleCouponCodeOnSubmit(code: string, shipping_profile_id: string) {
        try {
            const resp = await postCouponClaim({ code, shipping_profile_id });

            if (resp && resp.data) {
                const formattedCouponMap = resp.data.reduce((acc, coupon) => {
                    acc[coupon.id] = coupon;

                    return acc;
                }, {});

                setCouponMap(formattedCouponMap);
            }
        } catch (err) {
            if (err instanceof ValidationError) {
                const { context } = err;

                if (typeof context.detail === 'string') {
                    setError(context.detail);
                }
            }
        }
    }

    function resetCouponModalState() {
        setLoading(true);
        setFetching(true);
        setError('');
    }

    useEffect(() => {
        if (visible && isFetching) {
            fetchCouponMap().finally(() => {
                setLoading(false);
                setFetching(false);
            });
        }
    }, [visible, isFetching, fetchCouponMap, setCouponMap]);

    useEffect(() => {
        const selectedCoupon = couponMap[selectedCouponId];

        onChangeCouponId(selectedCoupon ? selectedCoupon.id : null);
    }, [onChangeCouponId, couponMap, selectedCouponId]);

    const couponList = useMemo(() => {
        return Object.values(couponMap);
    }, [couponMap]);

    const state = {
        error,
        isLoading,
        isFetching,
        couponMap,
        couponList,
        selectedCouponId,
    };

    const actions = {
        handleCouponCodeOnSubmit,
        handleSelectedCouponOnChange,
        getSelectedCouponCode,
        resetCouponModalState,
    };

    return {
        state,
        actions,
    };
}
